import { ArrowRight, Globe } from 'lucide-react';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParticipants } from '@/lib/api/ops-queries';
import { TONE_CLASSES, type StateTone } from '@/lib/states';
import { cn } from '@/lib/utils';
import { Pill } from '@/components/ui/primitives';

/**
 * Naming which website a row — or one side of a matched pair — belongs to.
 *
 * Cross-website matching means a row's own participant no longer tells the whole story: a deposit
 * owned by `web_a` can settle a withdrawal owned by `web_b`, and until this existed every screen
 * showed only one side, so an operator reading a case, a history row or a withdrawal had no way to
 * notice the counterparty was on a different site at all — the fact that made the case interesting
 * was invisible on the row that opened it.
 *
 * Two exports, one idea:
 *
 * - {@link ParticipantChip} renders one website id as a small `Pill`, toned like every other state
 *   chip in the console (`@/lib/states`' `TONE_CLASSES`) rather than as plain text — a row that
 *   already carries a `StateBadge` or a dual-control `Pill` should not have its one remaining fact
 *   render in a different register.
 * - {@link ParticipantPair} is what every call site actually reaches for: the depositor's website
 *   and the withdrawer's website together, calm when they agree and visibly — not just
 *   colour-wise — different when they do not. Colour alone would fail an operator reading in
 *   greyscale or without colour vision (WCAG 1.4.1, the same rule `state-badge.tsx` follows), so
 *   the cross-website case also carries an icon and a text marker, never colour by itself.
 */

/**
 * id → registered display name, from the participant directory.
 *
 * A context with an `null` default rather than `useParticipants()` inside the chip: the chip
 * renders in dense table cells and bare component tests, neither of which should need a query
 * client just to print a website's name. The operator shell mounts the provider once (the same
 * `/participants` query the web-scope selector already keeps warm), and a chip outside it — or
 * one rendered before the directory loads — simply shows the raw id, which is never wrong.
 */
const ParticipantNamesContext = createContext<ReadonlyMap<string, string> | null>(null);

/** Mounts the directory for every {@link ParticipantChip} below — see the context note. */
export function ParticipantDirectoryProvider({ children }: { children: ReactNode }): ReactNode {
  const participants = useParticipants();
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of participants.data?.items ?? []) map.set(p.id, p.name);
    return map;
  }, [participants.data]);
  return (
    <ParticipantNamesContext.Provider value={names}>{children}</ParticipantNamesContext.Provider>
  );
}

/** Props for {@link ParticipantChip}. */
export interface ParticipantChipProps {
  /** The participant id, e.g. `web_a`. */
  participant: string;
  /** Tone override. Defaults to `idle` — the calm, muted look a same-website chip wears. */
  tone?: StateTone;
  /** Small role word before the id (e.g. "ฝาก"/"ถอน") — the cross-web pair uses it so the arrow
   *  between the chips reads as a direction, not decoration. */
  roleLabel?: string | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/** One website, as a small pill. */
export function ParticipantChip({
  participant,
  tone = 'idle',
  roleLabel,
  className,
}: ParticipantChipProps): ReactNode {
  const names = useContext(ParticipantNamesContext);
  const name = names?.get(participant);
  // The registered display name when the directory has one, the raw id otherwise. The id every
  // log line and ticket uses stays one hover away — unless the name already carries it, the way
  // several participants register theirs ("Chao Phraya Gaming (web_b)").
  const title =
    name === undefined || name === participant || name.includes(participant)
      ? participant
      : `${name} (${participant})`;
  return (
    <Pill className={cn(TONE_CLASSES[tone].badge, 'rounded-full', className)} title={title}>
      <Globe className="size-3 shrink-0" aria-hidden="true" />
      {roleLabel != null && <span className="shrink-0 opacity-70">{roleLabel}</span>}
      <span className="max-w-[10rem] truncate">{name ?? participant}</span>
    </Pill>
  );
}

/** Props for {@link ParticipantPair}. */
export interface ParticipantPairProps {
  /** The depositor's website — `null`/`undefined` when unknown (an older API, or nothing to show). */
  depositor: string | null | undefined;
  /** The withdrawer's website — `null`/`undefined` when unmatched or unknown. */
  withdrawer: string | null | undefined;
  /** Additional classes. */
  className?: string | undefined;
}

/**
 * The depositor's and withdrawer's websites, together.
 *
 * Three shapes, depending on what is known:
 *
 * - **Only one side known** (the other `null`/`undefined`, e.g. a withdrawal still waiting for a
 *   match, or an API response that predates one of the two fields): that one side renders alone,
 *   calmly. This is not "cross-website unknown" — it is "there is only one fact to show" — so it
 *   never wears the attention tone.
 * - **Both known and equal**: a same-website match, which is still the overwhelming majority of
 *   rows. One calm chip, because repeating the same id twice would teach an operator to stop
 *   reading it.
 * - **Both known and different**: a genuine cross-website match. Both chips render in the
 *   `attention` tone with their roles named (ฝาก → ถอน), joined by a directional arrow — money
 *   moves from the depositor's website into the withdrawer's queue, and the row should read that
 *   way. The two chips + role words + arrow are themselves the non-colour signal (a same-web row
 *   is a single chip with none of that), so no extra text marker is needed to survive greyscale.
 */
export function ParticipantPair({ depositor, withdrawer, className }: ParticipantPairProps): ReactNode {
  const { t } = useTranslation();

  if (depositor == null && withdrawer == null) return null;

  const crossWeb = depositor != null && withdrawer != null && depositor !== withdrawer;

  if (!crossWeb) {
    const known = depositor ?? withdrawer;
    if (known == null) return null;
    return (
      <span className={cn('inline-flex min-w-0 items-center', className)} data-cross-web="false">
        <ParticipantChip participant={known} />
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex min-w-0 flex-wrap items-center gap-1.5', className)}
      data-cross-web="true"
      /* `title` rather than a Radix `Tooltip`: this chip is reached from dense table cells all
         over the console, several of which are not wrapped in `TooltipProvider` (bare component
         tests included), and a native title carries the same explanation without that
         dependency. */
      title={t('domain.participantChip.crossWebTooltip', { depositor, withdrawer })}
    >
      <ParticipantChip
        participant={depositor}
        tone="attention"
        roleLabel={t('domain.participantChip.depositorRole')}
      />
      <ArrowRight className="size-3.5 shrink-0 text-state-attention" aria-hidden="true" />
      <ParticipantChip
        participant={withdrawer}
        tone="attention"
        roleLabel={t('domain.participantChip.withdrawerRole')}
      />
    </span>
  );
}
